import { isMobileOnly } from 'react-device-detect';
import {
  aimHighOnAuto,
  rememberEstimate,
  rememberedEstimate,
} from './autoAimsHigh';
import { keepThePick } from './keepThePick';
import { keepLoadingAfterSeek } from './keepLoadingAfterSeek';

/**
 * The hls.js every player here uses.
 *
 * Pinned. Left alone this is fetched from a CDN at playback time, which
 * is a third party deciding when a player changes - and the wizard's
 * players were on 1.1.4 because nobody had told them otherwise, years
 * behind the library's and without what it had learnt since.
 */
export const HLS_LIBRARY =
  'https://cdn.jsdelivr.net/npm/hls.js@1.5.17/dist/hls.min.js';

interface Settling {
  /** Where to start, in seconds. */
  startAt: number;
  /** The player's quality list, read once hls.js exists. */
  qualities: () => any;
  /** Kept pointing at the hls.js instance playing now. */
  hlsRef: { current: any };
  /** For the diagnostics panel, where there is one. */
  note?: (text: string) => void;
}

/**
 * Settle hls.js before it loads anything.
 *
 * Shared by the library player and the wizard's, so that what one
 * learns the other gets: they play the same ladder from the same
 * service, and the wizard's player is the one used from a café.
 *
 * Two things have to be said here and cannot be said later. A
 * rendition still being built is served as an EVENT playlist, and
 * hls.js reads EVENT as something live and joins near the end of what
 * exists - a half-built film opened seven minutes in. Nothing served
 * here is live, so the start is said outright. Starting at zero and
 * seeking to the start point once it could play fetched a segment of
 * the opening for nothing, and made the seek one from 288p that hls.js
 * climbed out of slowly.
 *
 * And the version is pinned - see HLS_LIBRARY.
 */
export const settleHls = (
  provider: any,
  { startAt, qualities, hlsRef, note }: Settling,
) => {
  // Back on Auto after a pick by hand, after a seek on the top rung,
  // and at the start on a computer, try the top rung first where the
  // connection can carry it - see shared/autoAimsHigh.
  const remembered = isMobileOnly ? undefined : rememberedEstimate();
  provider.onInstance?.((hls: any) => {
    hlsRef.current = hls;
    hls.on('hlsDestroying', () => {
      if (hlsRef.current === hls) {
        hlsRef.current = null;
      }
    });
    const list = qualities();
    if (list) {
      // A quality picked by hand takes over at the next segment rather
      // than at once. At once, the player threw away all the picture it
      // had loaded and started again from the beginning of the segment
      // it was in - and Safari, given frames from a few seconds before
      // where playback was, showed them racing past to catch up before
      // carrying on. From the next segment it changes cleanly, within
      // about six seconds, and nothing already loaded is fetched twice.
      list.switch = 'next';
      // Which needs this beside it: until the pick takes over, the
      // old quality still playing would otherwise be taken for a new
      // pick and undo it. See shared/keepThePick.
      hls.on(
        'hlsDestroying',
        keepThePick(list, hls, (text) => note?.(text)),
      );
      hls.on(
        'hlsDestroying',
        aimHighOnAuto(hls, list, (text) => note?.(text)),
      );
    }
    if (!isMobileOnly) {
      hls.on('hlsDestroying', rememberEstimate(hls));
    }
    // On the iPhone a jump could leave hls.js loading nothing at all;
    // see shared/keepLoadingAfterSeek.
    hls.on(
      'hlsDestroying',
      keepLoadingAfterSeek(hls, (text) => note?.(text)),
    );
  });
  if (remembered) {
    note?.(
      `starting from this device's last estimate, ${(remembered / 1e6).toFixed(1)} Mbps`,
    );
  }
  provider.library = HLS_LIBRARY;
  provider.config = {
    ...(provider.config ?? {}),
    startPosition: startAt,
    ...(remembered ? { abrEwmaDefaultEstimate: remembered } : {}),
    // On a phone, do not reach for a rung wider than the screen
    // showing it.
    //
    // The ladder goes up to the file itself, which for a 4K concert is
    // 3840 wide with segments of thirteen megabytes. On a phone that is
    // useless and expensive: it spent five to nine seconds on each of
    // the top three rungs, abandoned them at 4%, 17% and 36%, walked
    // the whole ladder doing it and then stopped - having never asked
    // for a second segment of anything. The small rungs it did reach
    // arrived complete and immediately.
    //
    // Phones only. On a computer the same cap held a 4K concert at 720p
    // in a window and 1080p fullscreen on a LAN that could carry the
    // original several times over - the cap decides by the size of the
    // window, not by what the connection or the viewer wants. There,
    // bandwidth decides alone.
    capLevelToPlayerSize: isMobileOnly,
    // No cap on dropped frames (capLevelOnFPSDrop). It was on, and it
    // did harm: frames dropped while playback recovers from a stall
    // count the same as frames a device cannot decode, and the cap it
    // then sets lasts for the rest of the session. A computer that
    // played a 4K concert's original with no dropped frames for seven
    // minutes was capped at 480p after one stall, and Auto could not
    // climb above it again.
  };
};
